// Display rules for the golden-record review queue.
import { isMaskedValue } from "common/Utility";

export const REVIEW_STATUSES = ["OPEN", "RESOLVED", "DISMISSED", "SUPERSEDED"];

const TYPE_LABELS = {
    STALE: "Changed since search",
    STEWARD_CONFLICT: "Needs steward decision",
    MERGE_HELD: "Merge held for a steward",
    BIRTH_CANDIDATE: "Golden record not created",
    PK_COLLISION: "Primary key already golden",
    INVALID_KEY: "Matched on invalid values",
    SUBSUMPTION_REVIEW: "Duplicate not flagged as subsumed",
};

const TYPE_HELP = {
    BIRTH_CANDIDATE: "These records have no golden record yet, and their search creates golden records, but one was not created: equally trusted sources disagree on some fields, so a steward decides. Nothing was changed.",
    PK_COLLISION: "A golden record could not be created from these records: its primary key already belongs to another golden record. Fix the data, then run the search again.",
    INVALID_KEY: "No golden record was created from these records: a value they were matched on is not valid for its field (an invalid email, say). Fix the highlighted fields, then run the search again.",
    SUBSUMPTION_REVIEW: "This duplicate is in its survivor's dataset and was merged into it, but it was not flagged as subsumed: it holds values the survivor did not take, or it is the survivor of another search's cluster. It is kept, and no run retires it. Flag it as subsumed if the survivor has everything that matters, or dismiss to keep it separate.",
    STALE: "Records changed or were removed after the duplicate search ran, so deduplication left this cluster alone. Re-run the search to pick it up again.",
    STEWARD_CONFLICT: "Merged, but some field values were only settled by keeping the survivor's value. Check the highlighted fields.",
    MERGE_HELD: "Not merged yet, and nothing was changed: this merge involves a golden record, and a golden record is only merged automatically when every field has a single most-trusted value. Fields where equally trusted sources disagree (even ones a tie-breaker could settle) are sent to a steward first, and so is any merge that would delete a golden record.",
};

const REASON_LABELS = {
    MEMBER_MISSING: "A record no longer exists",
    MEMBER_CHANGED: "A record was edited",
    SURVIVOR_CHANGED: "The survivor was edited",
    ROWS_MISSING: "The search results were replaced",
    // Why the confidence gate held a merge (MERGE_HELD items).
    FIELD_CONFLICTS: "Some fields need a steward's choice",
    GOLDEN_WOULD_BE_DELETED: "A golden record would be deleted as a duplicate",
    // Why a golden record was not created (PK_COLLISION items).
    DUPLICATE_PRIMARY_KEY: "The primary key belongs to another golden record",
    // Why a golden record was not created (INVALID_KEY items).
    INVALID_KEY_VALUES: "A value matched on is not valid",
    // Why a duplicate was not flagged as subsumed (SUBSUMPTION_REVIEW items).
    VALUE_NOT_CARRIED: "It holds values the survivor did not take",
    SURVIVOR_ELSEWHERE: "It is the survivor of another search's cluster",
};

const STATUS_COLORS = { OPEN: "blue", RESOLVED: "green", DISMISSED: "default", SUPERSEDED: "orange" };

// A steward's actions on an open item; which ones an item allows comes with it (item.actions).
export const REVIEW_ACTIONS = { MERGE: "merge", DISMISS: "dismiss", BIRTH: "birth", SUBSUME: "subsume" };

// Why a steward took an action, as the duplicates service accepts them. OTHER needs a note.
export const REVIEW_REASON_CODES = {
    merge: ["VALUES_VERIFIED", "TRUSTED_SOURCE", "DATA_CORRECTED", "OTHER"],
    dismiss: ["NOT_DUPLICATES", "KEEP_AS_IS", "FIX_AT_SOURCE", "RERUN_SEARCH", "OTHER"],
    birth: ["VALUES_VERIFIED", "TRUSTED_SOURCE", "DATA_CORRECTED", "OTHER"],
    subsume: ["VALUES_VERIFIED", "DATA_CORRECTED", "OTHER"],
};

// The "Subsumed records" view (PRE-2837): why a steward retires flagged records, or restores one.
export const SUBSUMED_REASON_CODES = {
    retire: ["DUPLICATE_CONFIRMED", "VALUES_VERIFIED", "OTHER"],
    restore: ["NOT_DUPLICATE", "RETIRED_IN_ERROR", "DATA_CORRECTED", "OTHER"],
};

// At most this many flagged records are retired in one go.
export const MAX_RETIRE_SUBSUMED = 50;

const SUBSUMED_STATUS_LABELS = { SUBSUMED: "Flagged as subsumed", RETIRED: "Retired" };
const SUBSUMED_STATUS_COLORS = { SUBSUMED: "gold", RETIRED: "default" };

// What became of a record a steward asked to retire, when it was not retired.
const RETIRE_OUTCOME_LABELS = {
    NOT_FLAGGED: "no longer flagged",
    CHANGED: "edited since it was flagged; the next duplicate search and deduplication look at it again",
    NOT_FOUND: "it or its survivor no longer exists",
    SURVIVOR_ELSEWHERE: "it is the survivor of another search's cluster",
    FAILED: "could not be retired; try again",
    NOT_REACHED: "not reached this time; retire it again",
};

export function subsumedStatusLabel(status) {
    return SUBSUMED_STATUS_LABELS[status] || status;
}

export function subsumedStatusColor(status) {
    return SUBSUMED_STATUS_COLORS[status] || "default";
}

/**
 * What a steward is told after retiring flagged records: how many were retired, and why each other
 * one was not.
 * @param {{ results?: { dataObjectId: string, outcome: string }[], retired?: number }} result
 * @returns {{ type: "success" | "warning", text: string, notRetired: string[] }}
 */
export function retireOutcomeSummary(result) {
    const results = (result && result.results) || [];
    const notRetired = results
        .filter((entry) => entry.outcome !== "RETIRED")
        .map((entry) => `${entry.dataObjectId}: ${RETIRE_OUTCOME_LABELS[entry.outcome] || entry.outcome}`);
    const retired = (result && result.retired) || 0;
    return {
        type: notRetired.length > 0 ? "warning" : "success",
        text: `${retired} record(s) retired.${notRetired.length > 0 ? ` ${notRetired.length} not retired.` : ""}`,
        notRetired,
    };
}

const REASON_CODE_LABELS = {
    VALUES_VERIFIED: "Values checked and correct",
    TRUSTED_SOURCE: "Taken from the most trusted source",
    DATA_CORRECTED: "Values corrected",
    NOT_DUPLICATES: "Not duplicates",
    KEEP_AS_IS: "Keep as it is",
    FIX_AT_SOURCE: "To be fixed at the source",
    RERUN_SEARCH: "The search will be run again",
    DUPLICATE_CONFIRMED: "Confirmed duplicate",
    NOT_DUPLICATE: "Not a duplicate",
    RETIRED_IN_ERROR: "Retired by mistake",
    // A deduplication run retired the flagged record itself (its "retire now" option).
    RETIRED_BY_RUN: "Retired by the deduplication run",
    OTHER: "Other",
};

export const REVIEW_NOTE_MAX_LENGTH = 500;

export function reviewReasonCodeLabel(code) {
    return code ? REASON_CODE_LABELS[code] || code : "";
}

// Whether the item offers the action (only while it is open).
export function canTakeAction(item, action) {
    return !!item && item.status === "OPEN" && Array.isArray(item.actions) && item.actions.includes(action);
}

// Field types whose values a steward cannot type in (files and images).
const UNTYPEABLE_DATA_TYPES = ["image", "attachment"];
const NUMERIC_DATA_TYPES = ["integer", "float", "currency"];

/**
 * The fields a steward can set a value for: those the item asks them to check, except personal
 * data (masked, so they cannot see what they would be replacing) and files. Each comes with the
 * distinct visible values its members have, in member order, to pick from; the steward can also
 * type one (a steward conflict's duplicates are already gone, so only the survivor is left).
 * @returns {{ fieldId: string, name: string, dataType?: string, options: any[] }[]}
 */
export function overrideChoices(fields, item, records) {
    const members = ((item && item.members) || []).filter((member) => !member.redacted);
    const conflictFieldIds = new Set((item && item.conflictFieldIds) || []);
    return (fields || [])
        .filter((field) => conflictFieldIds.has(field.fieldId) && !field.isPII && !UNTYPEABLE_DATA_TYPES.includes(field.dataType))
        .map((field) => {
            const options = [];
            for (const member of members) {
                const record = records && records[member.dataObjectId];
                const value = record && !record.readError ? record[field.fieldId] : undefined;
                if (value === undefined || value === null || value === "" || typeof value === "object" || isMaskedValue(value)) {
                    continue;
                }
                if (!options.includes(value)) {
                    options.push(value);
                }
            }
            return { fieldId: field.fieldId, name: field.name || field.fieldId, dataType: field.dataType, options };
        });
}

/**
 * A value as the steward typed or picked it, in its field's type: numbers for numeric fields,
 * true/false for booleans, text otherwise. { error } when it is not a value of that type.
 * @returns {{ value: string | number | boolean } | { error: string }}
 */
export function overrideValue(dataType, text) {
    const trimmed = String(text).trim();
    if (NUMERIC_DATA_TYPES.includes(dataType)) {
        const number = Number(trimmed);
        if (trimmed === "" || Number.isNaN(number) || (dataType === "integer" && !Number.isInteger(number))) {
            return { error: dataType === "integer" ? "Enter a whole number." : "Enter a number." };
        }
        return { value: number };
    }
    if (dataType === "boolean") {
        if (!["true", "false"].includes(trimmed.toLowerCase())) {
            return { error: "Enter true or false." };
        }
        return { value: trimmed.toLowerCase() === "true" };
    }
    return { value: String(text) };
}

export const REVIEW_TYPES = Object.keys(TYPE_LABELS);

// What a masked value is shown as.
export const MASKED_TEXT = "Masked";

const JOB_TYPE_LABELS = {
    FIND_DATA_OBJECTS_DUPLICATES: "duplicate search",
    DATA_OBJECTS_DEDUPLICATES: "deduplication",
};

// "deduplication job J1", or "job J1" when the job's type is not known.
export function lockJobPhrase(lock) {
    const type = lock && lock.jobType ? JOB_TYPE_LABELS[lock.jobType] || lock.jobType.toLowerCase().replace(/_/g, " ") : "";
    return `${type ? `${type} ` : ""}job ${(lock && lock.jobId) || ""}`.trim();
}

export function reviewTypeLabel(type) {
    return TYPE_LABELS[type] || type;
}

export function reviewTypeHelp(type) {
    return TYPE_HELP[type] || "";
}

export function reviewReasonLabel(reason) {
    return reason ? REASON_LABELS[reason] || reason : "";
}

export function reviewStatusColor(status) {
    return STATUS_COLORS[status] || "default";
}

function displayValue(value) {
    if (value === undefined || value === null || value === "") {
        return "";
    }
    return typeof value === "object" ? JSON.stringify(value) : String(value);
}

/**
 * The rows of a side-by-side view of an item's members: one per schema field, with each visible
 * member's value in member order. A row "differs" when the members that have a value disagree;
 * "conflict" marks the fields the steward must look at. Personal data comes back masked (every
 * value the same "#####"), so a PII field, or any masked value, is shown as "masked" and never
 * compared: identical masks would look like agreement.
 * @param {object[]} fields      schema fields ({ fieldId, name }) in display order
 * @param {object} item          the review item
 * @param {object} records       member records by dataObjectId (a record, null, or { readError })
 */
export function comparisonRows(fields, item, records) {
    const members = ((item && item.members) || []).filter((member) => !member.redacted);
    const conflicts = new Set((item && item.conflictFieldIds) || []);
    return (fields || []).map((field) => {
        const raw = members.map((member) => {
            const record = records && records[member.dataObjectId];
            return record && !record.readError ? record[field.fieldId] : undefined;
        });
        const masked = !!field.isPII || raw.some((value) => isMaskedValue(value));
        const values = raw.map((value) => (masked && value !== undefined && value !== null && value !== "" ? MASKED_TEXT : displayValue(value)));
        const present = values.filter((value) => value !== "");
        return {
            fieldId: field.fieldId,
            name: field.name || field.fieldId,
            values,
            masked,
            differs: !masked && new Set(present).size > 1,
            conflict: conflicts.has(field.fieldId),
        };
    });
}

// How a member's column is headed: why its values are missing, when they are.
export function memberState(member, records) {
    if (member.redacted) {
        return "redacted";
    }
    const record = records && records[member.dataObjectId];
    if (record === null) {
        return "missing";
    }
    if (record && record.readError) {
        return "unreadable";
    }
    return record ? "loaded" : "loading";
}

/**
 * What a merge preview says about the confidence gate (golden records): null when the merge runs
 * as shown, otherwise an alert to show above the preview.
 * @param {{ held?: boolean, unknown?: boolean, reason?: string, fieldIds?: string[] } | null} gate
 * @param {{ fieldId: string, name?: string }[]} fields  the schema's fields, to name the held ones
 * @returns {{ type: "warning" | "info", text: string } | null}
 */
export function previewGateNotice(gate, fields) {
    if (!gate) {
        return null;
    }
    if (gate.unknown) {
        return { type: "info", text: "Whether a steward must review this merge could not be checked. Deduplication checks it again when it runs." };
    }
    if (gate.skipped) {
        return { type: "info", text: "This cluster has no golden record, so deduplication leaves it as it is (only golden records are changed)." };
    }
    if (!gate.held) {
        return null;
    }
    if (gate.reason === "GOLDEN_WOULD_BE_DELETED") {
        return { type: "warning", text: "This merge would delete a golden record, so deduplication will hold it for a steward. Nominate the golden record as the survivor to merge into it." };
    }
    const names = (gate.fieldIds || []).map((fieldId) => {
        const field = (fields || []).find((candidate) => candidate.fieldId === fieldId);
        return (field && field.name) || fieldId;
    });
    return {
        type: "warning",
        text: `Deduplication will hold this merge for a steward: equally trusted sources disagree on ${names.length ? names.join(", ") : "some fields"}. The values below are what the merge would pick.`,
    };
}
